import { getAppConfig } from '@services/config';
import { externalHttpService } from '@services/http';
import { CACHE_TAGS, HttpService } from '@services/http/http';

import { ReviewAverageType, ReviewsInfoType, SkeepersRatingResponse, SkeepersReviewResponse } from '@app-types/review';

import { mapSkeepersRatingsToAverages, mapSkeepersReviewsToInfo } from '@helpers/review';

type SkeepersRepositoryV2Config = {
    apiUrl: string;
    authUrl: string;
    clientId: string;
    clientSecret: string;
    ttl: number;
};

type SkeepersTokenResponse = {
    access_token: string;
    expires_in: number;
    token_type: string;
};

type CachedToken = {
    accessToken: string;
    expiresAt: number;
};

export class SkeepersRepositoryV2 {
    private readonly httpService: HttpService;
    private readonly configuration: SkeepersRepositoryV2Config;

    // OAuth token cache
    private cachedToken: CachedToken | null = null;
    private refreshPromise: Promise<string> | null = null;
    private readonly tokenRefreshBuffer = 60000; // Refresh 60s before expiry

    public constructor(httpService: HttpService, configuration: SkeepersRepositoryV2Config) {
        this.httpService = httpService;
        this.configuration = configuration;
    }

    // ============================================
    // OAuth2 Token Management
    // ============================================

    private async getAccessToken(): Promise<string> {
        if (this.isTokenValid()) {
            return this.cachedToken!.accessToken;
        }

        // Prevent concurrent refresh requests (mutex pattern)
        if (this.refreshPromise) {
            return this.refreshPromise;
        }

        this.refreshPromise = this.fetchNewToken();

        try {
            const token = await this.refreshPromise;
            return token;
        } finally {
            this.refreshPromise = null;
        }
    }

    private isTokenValid(): boolean {
        if (!this.cachedToken) return false;
        return Date.now() < this.cachedToken.expiresAt - this.tokenRefreshBuffer;
    }

    private async fetchNewToken(): Promise<string> {
        const credentials = Buffer.from(`${this.configuration.clientId}:${this.configuration.clientSecret}`).toString(
            'base64',
        );

        const response = await this.httpService.post<SkeepersTokenResponse>(
            this.configuration.authUrl,
            new URLSearchParams({
                grant_type: 'client_credentials',
                audience: 'verified-reviews',
            }),
            {
                headers: {
                    authorization: `Basic ${credentials}`,
                    'content-type': 'application/x-www-form-urlencoded',
                },
            },
        );

        this.cachedToken = {
            accessToken: response.access_token,
            expiresAt: Date.now() + response.expires_in * 1000,
        };

        return this.cachedToken.accessToken;
    }

    // ============================================
    // API Methods
    // ============================================

    /**
     * Get aggregated ratings for multiple products
     * Uses GET /products/ratings?type=product_variation_id&value=gencode1,gencode2,...
     * Max 200 products per request
     */
    public async getAverageReviews(gencodes: string[]): Promise<ReviewAverageType[] | undefined> {
        if (gencodes.length === 0) return [];

        try {
            const token = await this.getAccessToken();

            // Batch gencodes in chunks of 200 (API limit)
            const chunks = this.chunkArray(gencodes, 200);
            const allRatings: ReviewAverageType[] = [];

            for (const chunk of chunks) {
                const ratings = await this.httpService.get<SkeepersRatingResponse[]>(
                    `${this.configuration.apiUrl}/products/ratings`,
                    {
                        type: 'product_variation_id',
                        value: chunk.join(','),
                    },
                    {
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                        ttl: this.configuration.ttl,
                        tags: [CACHE_TAGS.PRODUCTS],
                    },
                );

                if (ratings) {
                    allRatings.push(...mapSkeepersRatingsToAverages(ratings));
                }
            }

            return allRatings;
        } catch {
            return undefined;
        }
    }

    /**
     * Get individual reviews for a product with pagination
     * Uses GET /products/reviews?product_variation_id=gencode&limit=X
     */
    public async getReviews(
        gencode: string,
        offset: number = 0,
        limit: number = 5,
    ): Promise<ReviewsInfoType | undefined> {
        try {
            const token = await this.getAccessToken();

            // Fetch product summary for overall rating and quality criteria
            const productSummary = await this.getProductRating(gencode, token);

            // Fetch individual reviews with pagination
            // Note: New API uses cursor-based pagination, but for offset/limit
            // we fetch enough to cover offset and slice
            const reviews = await this.httpService.get<SkeepersReviewResponse[]>(
                `${this.configuration.apiUrl}/products/reviews`,
                {
                    product_variation_id: gencode,
                    limit: Math.min(200, offset + limit).toString(),
                    sort: 'publish_date',
                    desc: 'publish_date',
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    ttl: this.configuration.ttl,
                    tags: [CACHE_TAGS.PRODUCT_DETAIL],
                },
            );

            if (reviews?.length > 0) {
                return mapSkeepersReviewsToInfo(reviews, productSummary, offset, limit);
            }

            return undefined;
        } catch {
            return undefined;
        }
    }

    /**
     * Get product rating summary including quality criteria
     */
    private async getProductRating(gencode: string, token: string): Promise<SkeepersRatingResponse | undefined> {
        try {
            const ratings = await this.httpService.get<SkeepersRatingResponse[]>(
                `${this.configuration.apiUrl}/products/ratings`,
                {
                    type: 'product_variation_id',
                    value: gencode,
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    ttl: this.configuration.ttl,
                    tags: [CACHE_TAGS.PRODUCT_DETAIL],
                },
            );

            return ratings?.[0];
        } catch {
            return undefined;
        }
    }

    private chunkArray<T>(array: T[], chunkSize: number): T[][] {
        const chunks: T[][] = [];
        for (let i = 0; i < array.length; i += chunkSize) {
            chunks.push(array.slice(i, i + chunkSize));
        }
        return chunks;
    }
}

const config = getAppConfig();

export const skeepersRepositoryV2 = new SkeepersRepositoryV2(externalHttpService, {
    apiUrl: config.skeepers.apiUrl!,
    authUrl: config.skeepers.authUrl!,
    clientId: config.skeepers.clientId!,
    clientSecret: config.skeepers.clientSecret!,
    ttl: parseInt(config.cache.ttl.productDetail),
});
